import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeRoot } from '@nyce/admin-kit';
import { ConfirmProvider, ConsoleShell, DRAWER_QUERY } from '@nyce/admin-kit/console';
import { logos } from './assets';
import { Button } from '@nyce/admin-kit/ui';
import { Login } from './Login';
import { OAUTH_AUTHORIZE_PATH, browser, finishSso, isSsoCallback } from './sso';
import { OAuthAuthorize } from './OAuthAuthorize';
import { EnvironmentSwitch, autoSsoRequested, withoutAutoSso } from './environment';
import { Loading } from './components/common';
import { DashboardPage } from './pages/Dashboard';
import { OperationsDashboardPage } from './pages/dashboard/OperationsPage';
import { IdentityDashboardPage } from './pages/dashboard/IdentityPage';
import { AdminDashboardPage } from './pages/dashboard/AdminPage';
import { buddyRoutes } from './buddy/routes';
import { printQueueRoutes } from './printqueue/routes';
import { automationRoutes } from './automation/routes';
import { designRoutes } from './design/routes';
import { billingRoutes } from './billing/routes';
import { onboardingRoutes } from './onboarding/routes';
import { indexAdvisorRoutes } from './indexadvisor/routes';
import { productionRoutes } from './prodstats/routes';
import { LinkedToolsPage } from './pages/LinkedTools';
import { LogsPage } from './pages/logs/LogsPage';
import { PodsPage } from './pages/pods/PodsPage';
import { PodLogsPage } from './pages/pods/PodLogsPage';
import { DatabasesPage } from './pages/databases/Databases';
import { JobPage } from './pages/databases/JobPage';
import { AuditPage } from './pages/admin/Audit';
import { ClustersPage } from './pages/admin/Clusters';
import { DbServersPage } from './pages/admin/DbServers';
import { EnvironmentsPage } from './pages/admin/Environments';
import { LinksPage } from './pages/admin/Links';
import { CustomersPage } from './pages/identity/Customers';
import { TenantsPage } from './pages/identity/Tenants';
import { TenantDetailPage } from './pages/identity/TenantDetail';
import { AccessPage } from './pages/identity/Access';
import { LoginAppsPage } from './pages/identity/LoginApps';
import { ScimEndpointsPage } from './pages/identity/ScimEndpoints';
import { StatisticsDashboardPage } from './pages/statistics/StatisticsPage';
import { OrganizationsPage } from './pages/statistics/Organizations';
import { OrganizationPage } from './pages/statistics/Organization';
import { DiagnosticsPage, InstallationPage } from './pages/statistics/Installation';
import { IncidentsPage } from './pages/statistics/Incidents';
import { ErrorGroupPage, ErrorGroupsPage } from './pages/statistics/ErrorGroups';
import { ReportsPage } from './pages/statistics/Reports';
import { StatisticsScreenPage } from './pages/statistics/Screen';
import { ImportPmsPage } from './pages/statistics/ImportPms';
import { TargetShell } from './identity/TargetShell';
import { ThemeToggle, useTheme } from './theme';
import { HOME, NAV, canOpen, visibleNav } from './nav';
import { api, clearLogin, applyMe, logout, session, useHubLogin, type HubLogin } from './session';

export { NAV, HOME, canOpen, visibleNav };
export type { NavNode } from './nav';

/** Pages shown without the sidebar (the office TV screen). */
export const KIOSK_PATHS = ['/statistics/screen'];

/** Below this width the sidebar becomes a top bar with a drawer. */
export { DRAWER_QUERY };

const LOGO_ALT = 'nyceOperations';

const roleLabel = (login: HubLogin) =>
  login.scopes.some((s) => s === 'ophub:*' || s === 'ophub:admin') ? 'hub admin' : 'operator';

const Logo = () => <img src={logos.logoWhite} alt={LOGO_ALT} className="nyk-logo oph-logo" />;

function UserBox({ login, onLogout }: { login: HubLogin; onLogout: () => void }) {
  return (
    <div className="nyk-user">
      <span className="nyk-user-name">
        {login.username}
        <br />
        <small>{roleLabel(login)}</small>
      </span>
      <div className="nyk-user-actions">
        <Button size="sm" variant="ghost" onClick={onLogout}>
          Sign out
        </Button>
        <ThemeToggle />
      </div>
    </div>
  );
}

export function Layout({
  login,
  children,
  onLogout,
}: {
  login: HubLogin;
  children: React.ReactNode;
  onLogout: () => void;
}) {
  const { pathname, search } = useLocation();
  if (KIOSK_PATHS.includes(pathname)) return <main className="oph-kiosk-main">{children}</main>;
  return (
    <ConsoleShell
      logo={<Logo />}
      nav={visibleNav(login.scopes)}
      home={HOME}
      pathname={pathname}
      Link={NavLink}
      navHeader={() => <EnvironmentSwitch pathname={pathname} search={search} />}
      userBox={() => <UserBox login={login} onLogout={onLogout} />}
      topbarEnd={login.username}
    >
      {children}
    </ConsoleShell>
  );
}

/** Routes for the pages the caller may open; everything else falls back to the dashboard. */
export function AppRoutes({ login }: { login: HubLogin }) {
  const allowed = (path: string) => canOpen(login.scopes, path);
  return (
    <Routes>
      <Route path="/" element={<Navigate to={HOME} replace />} />
      <Route path="/dashboard" element={<DashboardPage login={login} />} />
      {allowed('/operations') && (
        <Route path="/operations" element={<OperationsDashboardPage login={login} />} />
      )}
      {allowed('/statistics') && (
        <Route path="/statistics" element={<StatisticsDashboardPage scopes={login.scopes} />} />
      )}
      {productionRoutes(allowed, login.scopes)}
      {allowed('/statistics/orgs') && (
        <>
          <Route path="/statistics/orgs" element={<OrganizationsPage />} />
          <Route path="/statistics/orgs/:customerId" element={<OrganizationPage />} />
          <Route path="/statistics/installations/:customerId/:workserver" element={<InstallationPage />} />
          <Route
            path="/statistics/installations/:customerId/:workserver/diagnostics/:id"
            element={<DiagnosticsPage />}
          />
          <Route path="/statistics/incidents" element={<IncidentsPage />} />
          <Route path="/statistics/errors" element={<ErrorGroupsPage />} />
          <Route path="/statistics/errors/:fingerprint" element={<ErrorGroupPage />} />
          <Route path="/statistics/reports" element={<ReportsPage />} />
          <Route path="/statistics/screen" element={<StatisticsScreenPage />} />
        </>
      )}
      {allowed('/statistics/import') && <Route path="/statistics/import" element={<ImportPmsPage />} />}
      {buddyRoutes(allowed('/buddy'), allowed)}
      {printQueueRoutes(allowed('/printqueue'), allowed)}
      {automationRoutes(allowed('/automation'), allowed)}
      {designRoutes(allowed)}
      {billingRoutes(allowed('/billing'), allowed)}
      {onboardingRoutes(allowed('/onboarding'), allowed)}
      {allowed('/identity') && <Route path="/identity" element={<IdentityDashboardPage />} />}
      {allowed('/admin') && <Route path="/admin" element={<AdminDashboardPage />} />}
      {allowed('/logs') && <Route path="/logs" element={<LogsPage />} />}
      {allowed('/pods') && <Route path="/pods" element={<PodsPage login={login} />} />}
      {allowed('/pods') && <Route path="/pods/logs" element={<PodLogsPage />} />}
      {allowed('/databases') && <Route path="/databases" element={<DatabasesPage scopes={login.scopes} />} />}
      {allowed('/databases') && (
        <Route path="/databases/jobs/:id" element={<JobPage scopes={login.scopes} />} />
      )}
      {indexAdvisorRoutes(allowed, login.scopes)}
      {allowed('/identity/tenants') && (
        <>
          <Route
            path="/identity/tenants"
            element={
              <TargetShell>
                <TenantsPage />
              </TargetShell>
            }
          />
          <Route
            path="/identity/tenants/:id"
            element={
              <TargetShell>
                <TenantDetailPage />
              </TargetShell>
            }
          />
          <Route
            path="/identity/customers"
            element={
              <TargetShell>
                <CustomersPage />
              </TargetShell>
            }
          />
          <Route
            path="/identity/login-apps"
            element={
              <TargetShell>
                <LoginAppsPage />
              </TargetShell>
            }
          />
          <Route
            path="/identity/access"
            element={
              <TargetShell>
                <AccessPage />
              </TargetShell>
            }
          />
        </>
      )}
      {allowed('/identity/scim') && <Route path="/identity/scim" element={<ScimEndpointsPage />} />}
      <Route path="/tools" element={<LinkedToolsPage />} />
      {allowed('/admin/clusters') && <Route path="/admin/clusters" element={<ClustersPage />} />}
      {allowed('/admin/environments') && <Route path="/admin/environments" element={<EnvironmentsPage />} />}
      {allowed('/admin/db-servers') && <Route path="/admin/db-servers" element={<DbServersPage />} />}
      {allowed('/admin/links') && <Route path="/admin/links" element={<LinksPage />} />}
      {allowed('/admin/audit') && <Route path="/admin/audit" element={<AuditPage />} />}
      <Route path="*" element={<Navigate to={HOME} replace />} />
    </Routes>
  );
}

export default function App() {
  const login = useHubLogin();
  const { resolved } = useTheme();
  const signedIn = !!login;
  // Back from a federated sign-in (`/login/callback`): finish it before anything routes.
  const [sso, setSso] = useState<{ pending: boolean; error?: string }>(() => ({ pending: isSsoCallback() }));
  useEffect(() => {
    if (!sso.pending) return;
    void finishSso().then((r) => {
      if (r.external) {
        browser.go(r.next);
        return;
      }
      window.history.replaceState(null, '', r.next);
      setSso({ pending: false, error: r.hub ? r.error : undefined });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // came from another hub's switch with a session here already: drop the marker
    if (signedIn && autoSsoRequested())
      window.history.replaceState(null, '', withoutAutoSso(window.location.pathname, window.location.search));
  }, [signedIn]);
  useEffect(() => {
    if (!signedIn) return;
    const stop = session.startAutoRefresh();
    const check = () =>
      api
        .me()
        .then(applyMe)
        .catch((e: { status?: number }) => {
          if (e.status === 401 || e.status === 403) clearLogin();
        });
    void check();
    const id = setInterval(check, 2 * 60 * 1000);
    return () => {
      stop();
      clearInterval(id);
    };
  }, [signedIn]);

  if (sso.pending) return <Loading loading />;
  // MCP consent (docs/mcp.md): its own Entra sign-in, with or without a console session
  if (window.location.pathname === OAUTH_AUTHORIZE_PATH) return <OAuthAuthorize />;
  if (!login) return <Login error={sso.error} />;
  return (
    <ThemeRoot theme={resolved} style={{ background: 'transparent' }}>
      <BrowserRouter>
        <ConfirmProvider>
          <Layout login={login} onLogout={() => void logout()}>
            <AppRoutes login={login} />
          </Layout>
        </ConfirmProvider>
      </BrowserRouter>
    </ThemeRoot>
  );
}
